/*
All material copyright ESRI, All Rights Reserved, unless otherwise specified.
See https://js.arcgis.com/4.28/esri/copyright.txt for details.
*/
import { _ as e } from "../chunks/tslib.es6.js";
import { property as t } from "../core/accessorSupport/decorators/property.js";
import "../core/accessorSupport/ensureType.js";
import "../core/arrayUtils.js";
import "../core/has.js";
import { subclass as r } from "../core/accessorSupport/decorators/subclass.js";
import s from "../layers/FeatureLayer.js";
import { queryAllJSON as a } from "../layers/support/featureQueryAll.js";
import o from "./Network.js";
import i from "./RulesTable.js";
import n from "./support/NamedTraceConfiguration.js";
import u from "./support/NetworkSystemLayers.js";
import l from "./support/TerminalConfiguration.js";
import m from "./support/TraceJobInfo.js";
import { RuleType as d } from "./support/typeUtils.js";
import c from "../rest/support/Query.js";
let y = class extends o {
  constructor(e) {
    super(e), this.sharedNamedTraceConfigurations = [], this.type = "utility";
  }
  get serviceTerritoryFeatureLayerId() {
    return this.dataElement?.serviceTerritoryFeatureLayerId ?? null;
  }
  get networkSystemLayers() {
    return new u({
      rulesTableId: this.sourceJSON?.systemLayers.rulesTableId,
      rulesTableUrl: this.sourceJSON ? `${this.featureServiceUrl}/${this.sourceJSON?.systemLayers.rulesTableId}` : null,
      subnetworksTableId: this.sourceJSON?.systemLayers.subnetworksTableId,
      subnetworksTableUrl: this.sourceJSON ? `${this.featureServiceUrl}/${this.sourceJSON?.systemLayers.subnetworksTableId}` : null,
      dirtyAreasLayerId: this.sourceJSON?.systemLayers.dirtyAreasLayerId,
      dirtyAreasLayerUrl: this.sourceJSON ? `${this.featureServiceUrl}/${this.sourceJSON?.systemLayers.dirtyAreasLayerId}` : null
    });
  }
  get terminalConfigurations() {
    return this.dataElement?.terminalConfigurations.map(e => l.fromJSON(e)) || [];
  }
  get domainNetworkNames() {
    return this.dataElement?.domainNetworks.map(e => e.domainNetworkName) || [];
  }
  get _utilityLayerList() {
    const e = new Set();
    return this.dataElement?.domainNetworks?.map(t => {
      t.edgeSources.map(t => {
        e.add(t.layerId);
      }), t.junctionSources.map(t => {
        e.add(t.layerId);
      });
    }), e;
  }
  async load(e) {
    return this.addResolvingPromise(super.load(e)), this.addResolvingPromise(this._loadNamedTraceConfigurationsFromNetwork(e)), this;
  }
  getTerminalConfiguration(e) {
    let t = null,
      r = null;
    const s = e.layer;
    let a = null;
    if ("feature" === s?.type) {
      if (a = s.layerId, null === a) return null;
    } else {
      if ("subtype-sublayer" !== s?.type) return null;
      if (a = s?.parent?.layerId ?? null, null === a) return null;
    }
    const o = e.attributes;
    if (null == o) return null;
    for (const u of Object.keys(o)) "ASSETGROUP" === u.toUpperCase() && (t = e.getAttribute(u)), "ASSETTYPE" === u.toUpperCase() && (r = e.getAttribute(u));
    if (!this.dataElement) return null;
    let i = null;
    const n = this.dataElement.domainNetworks;
    for (const u of n) {
      const e = u.junctionSources?.find(e => e.layerId === a);
      if (e) {
        const s = e.assetGroups?.find(e => e.assetGroupCode === t);
        if (s) {
          const e = s.assetTypes?.find(e => e.assetTypeCode === r);
          if (e) {
            i = e.terminalConfigurationId;
            break;
          }
        }
      }
    }
    if (null != i) {
      const e = this.dataElement.terminalConfigurations,
        t = e?.find(e => e.terminalConfigurationId === i);
      return t ? l.fromJSON(t) : null;
    }
    return null;
  }
  getTierNames(e) {
    const t = this.dataElement?.domainNetworks.find(t => t.domainNetworkName === e);
    return t?.tiers.map(e => e.name) || [];
  }
  async getRulesTable() {
    return this._sharedRulesTable || (this._sharedRulesTable = this._createRulesTable()), await this._sharedRulesTable;
  }
  isUtilityLayer(e) {
    return this._utilityLayerList.has(e.layerId);
  }
  async queryAssociations(e) {
    const [{
        queryAssociations: t
      }, {
        default: r
      }] = await Promise.all([import("../rest/networks/queryAssociations.js"), import("../rest/networks/support/QueryAssociationsParameters.js")]),
      s = r.from(e);
    s.gdbVersion = this.gdbVersion, s.moment = this.historicMoment;
    return (await t(this.networkServiceUrl, s)).associations;
  }
  async synthesizeAssociationGeometries(e) {
    const [{
        synthesizeAssociationGeometries: t
      }, {
        default: r
      }] = await Promise.all([import("../rest/networks/synthesizeAssociationGeometries.js"), import("../rest/networks/support/SynthesizeAssociationGeometriesParameters.js")]),
      s = r.from(e);
    return s.gdbVersion = this.gdbVersion, s.moment = this.historicMoment, t(this.networkServiceUrl, s);
  }
  async trace(e) {
    const [{
        trace: t
      }, {
        default: r
      }] = await Promise.all([import("../rest/networks/trace.js"), import("../rest/networks/support/TraceParameters.js")]),
      s = r.from(e);
    return s.gdbVersion = this.gdbVersion, s.moment = this.historicMoment, t(this.networkServiceUrl, s);
  }
  async submitTraceJob(e) {
    const [{
        submitTraceJob: t
      }, {
        default: r
      }] = await Promise.all([import("../rest/networks/trace.js"), import("../rest/networks/support/TraceParameters.js")]),
      s = r.from(e);
    s.gdbVersion = this.gdbVersion, s.moment = this.historicMoment;
    const a = await t(this.networkServiceUrl, s);
    return new m({
      statusUrl: a
    });
  }
  async _loadNamedTraceConfigurationsFromNetwork(e) {
    if (0 === this.sharedNamedTraceConfigurations?.length) return;
    const t = this.sharedNamedTraceConfigurations.map(e => e.globalId),
      r = await this.queryNamedTraceConfigurations({
        globalIds: t
      }, e);
    for (const s of this.sharedNamedTraceConfigurations) {
      const e = r?.find(e => e.globalId === s.globalId);
      if (e) {
        const t = e.write({}, {
          origin: "service"
        });
        s.read(t, {
          origin: "service"
        });
      }
    }
  }
  async _createRulesTable() {
    const e = this.networkSystemLayers.rulesTableUrl,
      t = new s({
        url: e
      });
    await t.load();
    const r = this.dataElement?.domainNetworks;
    if (!r) return null;
    const a = r.flatMap(e => [...e.edgeSources, ...e.junctionSources]),
      o = (await this._queryRulesTable(t)).map(e => this._hydrateRuleInfo(t, a, e));
    return new i({
      layer: t,
      rules: o
    });
  }
  async _queryRulesTable(e) {
    const t = new c({
      where: "1=1",
      outFields: ["*"]
    });
    return (await a(e, t)).features;
  }
  _hydrateRuleInfo(e, t, r) {
    const s = e.fieldsIndex,
      a = s.get("RULETYPE"),
      o = s.get("CREATIONDATE"),
      i = s.get("FROMNETWORKSOURCEID"),
      n = s.get("FROMASSETGROUP"),
      u = s.get("FROMASSETTYPE"),
      l = s.get("FROMTERMINALID"),
      m = s.get("TONETWORKSOURCEID"),
      c = s.get("TOASSETGROUP"),
      y = s.get("TOASSETTYPE"),
      p = s.get("TOTERMINALID"),
      f = s.get("VIANETWORKSOURCEID"),
      T = s.get("VIAASSETGROUP"),
      h = s.get("VIAASSETTYPE"),
      g = s.get("VIATERMINALID"),
      b = r.attributes[a.name],
      w = new Date(r.attributes[o.name]),
      I = [{
        networkSourceId: r.attributes[i.name],
        assetGroupId: r.attributes[n.name],
        assetTypeId: r.attributes[u.name],
        terminalId: r.attributes[l.name]
      }, {
        networkSourceId: r.attributes[m.name],
        assetGroupId: r.attributes[c.name],
        assetTypeId: r.attributes[y.name],
        terminalId: r.attributes[p.name]
      }, {
        networkSourceId: r.attributes[f.name],
        assetGroupId: r.attributes[T.name],
        assetTypeId: r.attributes[h.name],
        terminalId: r.attributes[g.name]
      }];
    let S;
    !function (e) {
      e[e.from = 0] = "from", e[e.to = 1] = "to", e[e.via = 2] = "via";
    }(S || (S = {}));
    const N = {
      ruleType: b,
      creationDate: w
    };
    for (const k of [S.from, S.to, S.via]) {
      if (b !== d.RTEdgeJunctionEdgeConnectivity && k === S.via) continue;
      const e = I[k],
        r = t.find(t => t.sourceId === e.networkSourceId),
        s = r?.assetGroups.find(t => t.assetGroupCode === e.assetGroupId),
        a = s?.assetTypes.find(t => t.assetTypeCode === e.assetTypeId);
      let o = this._getTerminal(b, a, e);
      b !== d.RTContainment && b !== d.RTAttachment || (o = null);
      let i = "";
      switch (k) {
        case S.from:
          i = "from";
          break;
        case S.to:
          i = "to";
          break;
        case S.via:
          i = "via";
      }
      N[`${i}NetworkSource`] = r, N[`${i}AssetGroup`] = s, N[`${i}AssetType`] = a, N[`${i}Terminal`] = o?.toJSON();
    }
    return N;
  }
  _getTerminal(e, t, r) {
    if (e === d.RTAttachment || e === d.RTContainment) return null;
    const s = t?.terminalConfigurationId,
      a = this.terminalConfigurations?.find(e => e.id === s);
    return a?.terminals?.find(e => e.id === r.terminalId) ?? null;
  }
};
e([t({
  type: [n],
  json: {
    origins: {
      "web-map": {
        read: {
          source: "traceConfigurations"
        },
        write: {
          target: "traceConfigurations"
        }
      },
      service: {
        read: {
          source: "traceConfigurations"
        }
      }
    },
    read: !1
  }
})], y.prototype, "sharedNamedTraceConfigurations", void 0), e([t({
  type: ["utility"],
  readOnly: !0,
  json: {
    read: !1,
    write: !1
  }
})], y.prototype, "type", void 0), e([t({
  readOnly: !0
})], y.prototype, "serviceTerritoryFeatureLayerId", null), e([t({
  readOnly: !0
})], y.prototype, "networkSystemLayers", null), e([t({
  readOnly: !0
})], y.prototype, "terminalConfigurations", null), e([t({
  readOnly: !0
})], y.prototype, "domainNetworkNames", null), y = e([r("esri.networks.UtilityNetwork")], y);
const p = y;
export { p as default };